import React, { useCallback, useState, useRef, Fragment, Component } from 'react';
import { motion, useInView, AnimatePresence, useScroll, useMotionValueEvent, useTransform } from 'framer-motion';
import { ShieldCheckIcon, AlertTriangleIcon, MapPinIcon, CheckIcon, LandmarkIcon, GlobeIcon, FileTextIcon, BookOpenIcon, ArrowRightIcon, LayoutDashboardIcon, FolderIcon, SettingsIcon, LeafIcon, CheckCircle2Icon, ClipboardListIcon, BarChart3Icon, DownloadIcon } from 'lucide-react';
import { MAILTO } from '../../utils/mailto';
interface TimelineEntry {
  year: string;
  icon: React.ReactNode;
  tag: string;
  tagColor: string;
  tagColorDark: string;
  title: string;
  description: string;
  status: 'active' | 'upcoming' | 'in-progress' | 'future';
  isNow?: boolean;
}
const timeline: TimelineEntry[] = [{
  year: '2011',
  icon: <FileTextIcon className="w-7 h-7" data-id="element-145" />,
  tag: 'Uganda',
  tagColor: 'bg-amber-100 text-amber-700',
  tagColorDark: 'bg-amber-900/30 text-amber-300',
  title: 'UCDA Quality Standards',
  description: 'The Uganda Coffee Development Authority establishes mandatory quality grading (Drugar, Bugisu AA, Wugar) and export certification, laying the foundation of Ugandan coffee traceability.',
  status: 'active'
}, {
  year: '2021',
  icon: <LandmarkIcon className="w-7 h-7" data-id="element-146" />,
  tag: 'Uganda',
  tagColor: 'bg-amber-100 text-amber-700',
  tagColorDark: 'bg-amber-900/30 text-amber-300',
  title: 'National Coffee Act Enacted',
  description: 'Requires mandatory registration of all coffee value chain actors, including farmers, processors, traders, and exporters. Unregistered operators face penalties and loss of export licenses.',
  status: 'active'
}, {
  year: '2023',
  icon: <GlobeIcon className="w-7 h-7" data-id="element-147" />,
  tag: 'International',
  tagColor: 'bg-purple-100 text-purple-700',
  tagColorDark: 'bg-purple-900/30 text-purple-300',
  title: 'ICO Resolution 420',
  description: 'The International Coffee Organization requires member countries including Uganda to implement traceability systems for quality verification and sustainability reporting.',
  status: 'in-progress'
}, {
  year: '2024',
  icon: <BookOpenIcon className="w-7 h-7" data-id="element-148" />,
  tag: 'Uganda + EU',
  tagColor: 'bg-blue-100 text-blue-700',
  tagColorDark: 'bg-blue-900/30 text-blue-300',
  title: 'EUDR Preparation Begins',
  description: 'Uganda launches nationwide pilot programs for farm-level GPS mapping and digital due diligence systems. Over 200,000 farms geo-tagged in Mt. Elgon and Rwenzori regions.',
  status: 'in-progress',
  isNow: true
}, {
  year: 'Dec 2025',
  icon: <GlobeIcon className="w-7 h-7" data-id="element-149" />,
  tag: 'EU',
  tagColor: 'bg-red-100 text-red-700',
  tagColorDark: 'bg-red-900/30 text-red-300',
  title: 'EUDR Enforcement Deadline',
  description: 'All coffee exported to the EU must include geolocation data proving deforestation-free sourcing. Uganda exported $900M+ of coffee to the EU in 2023, and non-compliance risks losing this critical market.',
  status: 'upcoming'
}, {
  year: '2026+',
  icon: <ShieldCheckIcon className="w-7 h-7" data-id="element-150" />,
  tag: 'Uganda',
  tagColor: 'bg-emerald-100 text-emerald-700',
  tagColorDark: 'bg-emerald-900/30 text-emerald-300',
  title: 'Full Digital Traceability Mandate',
  description: 'Uganda targets 100% digital traceability across all 1.7 million smallholder households, with blockchain-verified records from cherry to export container.',
  status: 'future'
}];
const complianceChecklist = ['Farm-level GPS coordinates for all sourcing regions', 'Farmer registration with National Coffee Act ID', 'Deforestation-free due diligence statements', 'UCDA quality grade certification per lot', 'Carbon footprint calculation per export batch', 'Supply chain risk assessment documentation', 'Digital audit trail from cherry to green bean', 'Annual sustainability impact report'];
const statusConfig = {
  active: {
    dot: 'bg-emerald-500',
    label: 'Active',
    labelColorDark: 'text-emerald-300 bg-emerald-900/40',
    labelColorLight: 'text-emerald-700 bg-emerald-100'
  },
  'in-progress': {
    dot: 'bg-blue-500',
    label: 'In Progress',
    labelColorDark: 'text-blue-300 bg-blue-900/40',
    labelColorLight: 'text-blue-700 bg-blue-100'
  },
  upcoming: {
    dot: 'bg-amber-500',
    label: 'Upcoming',
    labelColorDark: 'text-amber-300 bg-amber-900/40',
    labelColorLight: 'text-amber-700 bg-amber-100'
  },
  future: {
    dot: 'bg-carbon-bright',
    label: 'Planned',
    labelColorDark: 'text-carbon-bright bg-carbon-bright/15',
    labelColorLight: 'text-emerald-700 bg-emerald-100'
  }
};
/* ─── Circular Progress Ring ─── */
function ProgressRing({
  completed,
  total
}: {
  completed: number;
  total: number;
}) {
  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  const progress = completed / total * circumference;
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  return <div ref={ref} className="relative w-24 h-24 mx-auto" data-id="element-151">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 96 96" data-id="element-152">
        <circle cx="48" cy="48" r={radius} fill="none" stroke="#E8F5E9" strokeWidth="5" data-id="element-153" />

        <motion.circle cx="48" cy="48" r={radius} fill="none" stroke="#52B788" strokeWidth="5" strokeLinecap="round" strokeDasharray={circumference} initial={{
        strokeDashoffset: circumference
      }} animate={isInView ? {
        strokeDashoffset: circumference - progress
      } : {}} transition={{
        duration: 1.5,
        delay: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-154" />

      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-155">
        <span className="text-xl font-bold text-carbon-deep" data-id="element-156">
          {completed}/{total}
        </span>
        <span className="text-[10px] font-semibold text-carbon-deep uppercase tracking-wider" data-id="element-157">
          Complete
        </span>
      </div>
    </div>;
}
/* ─── Animated Check Item ─── */
function ChecklistItem({
  text,
  index,
  isInView
}: {
  text: string;
  index: number;
  isInView: boolean;
}) {
  return <motion.li className={`flex items-start gap-3.5 px-4 py-3.5 rounded-xl ${index % 2 === 0 ? 'bg-carbon-geo/40' : 'bg-transparent'}`} initial={{
    opacity: 0,
    x: 16
  }} animate={isInView ? {
    opacity: 1,
    x: 0
  } : {}} transition={{
    delay: 0.5 + index * 0.1,
    duration: 0.5,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-158">

      <motion.div className="relative flex-shrink-0 w-6 h-6 rounded-full bg-carbon-bright flex items-center justify-center mt-0.5" initial={{
      scale: 0
    }} animate={isInView ? {
      scale: 1
    } : {}} transition={{
      delay: 0.7 + index * 0.1,
      duration: 0.4,
      type: 'spring',
      stiffness: 300,
      damping: 15
    }} data-id="element-159">

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        delay: 0.9 + index * 0.1,
        duration: 0.3
      }} data-id="element-160">

          <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-161" />
        </motion.div>
      </motion.div>
      <div className="flex-1 min-w-0" data-id="element-162">
        <div className="flex items-baseline gap-2" data-id="element-163">
          <span className="text-xs font-bold text-carbon-bright tabular-nums" data-id="element-164">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="text-sm font-medium text-carbon-deep leading-snug" data-id="element-165">
            {text}
          </span>
        </div>
      </div>
    </motion.li>;
}
/* ─── Full-Screen Immersive Timeline ─── */
function ImmersiveTimeline() {
  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const runwayRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: runwayRef,
    offset: ['start start', 'end end']
  });
  const progressWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    const stepFloat = latest * (timeline.length - 1);
    const newStep = Math.min(Math.round(stepFloat), timeline.length - 1);
    if (newStep !== activeStep) {
      setDirection(newStep > activeStep ? 1 : -1);
      setActiveStep(newStep);
    }
  });
  const goTo = useCallback((index: number) => {
    setDirection(index > activeStep ? 1 : -1);
    setActiveStep(index);
  }, [activeStep]);
  const entry = timeline[activeStep];
  const config = statusConfig[entry.status];
  const contentVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      y: dir > 0 ? 60 : -60
    }),
    center: {
      opacity: 1,
      y: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      y: dir > 0 ? -60 : 60
    })
  };
  const yearBgVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      scale: 0.85,
      y: dir > 0 ? 40 : -40
    }),
    center: {
      opacity: 0.08,
      scale: 1,
      y: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      scale: 1.1,
      y: dir > 0 ? -40 : 40
    })
  };
  return <div ref={runwayRef} className="relative" style={{
    height: `${timeline.length * 85}vh`
  }} data-id="element-166">

      {/* Sticky full-screen container */}
      <div className="sticky top-0 h-screen w-full bg-carbon-deep overflow-hidden" data-id="element-167">
        {/* Subtle grid texture */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" aria-hidden="true" data-id="element-168">

          <defs data-id="element-169">
            <pattern id="tl-grid" width="60" height="60" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-170">

              <line x1="0" y1="0" x2="0" y2="60" stroke="#52B788" strokeWidth="0.5" data-id="element-171" />

              <line x1="0" y1="0" x2="60" y2="0" stroke="#52B788" strokeWidth="0.5" data-id="element-172" />

            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#tl-grid)" data-id="element-173" />
        </svg>

        {/* Main content area */}
        <div className="relative z-10 h-full flex flex-col" data-id="element-174">
          {/* Content — centered vertically */}
          <div className="flex-1 flex items-center" data-id="element-175">
            <div className="w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16" data-id="element-176">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-177">
                {/* Left: Year + Icon */}
                <div className="relative" data-id="element-178">
                  {/* Giant background year */}
                  <AnimatePresence mode="wait" custom={direction} data-id="element-179">
                    <motion.div key={`bg-${activeStep}`} custom={direction} variants={yearBgVariants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="absolute -top-8 -left-4 select-none pointer-events-none" aria-hidden="true" data-id="element-180">

                      <span className="text-[10rem] sm:text-[14rem] lg:text-[18rem] font-bold text-carbon-bright leading-none tracking-tighter" data-id="element-181">
                        {entry.year.replace('Dec ', '').replace('+', '')}
                      </span>
                    </motion.div>
                  </AnimatePresence>

                  {/* Foreground content */}
                  <AnimatePresence mode="wait" custom={direction} data-id="element-182">
                    <motion.div key={`left-${activeStep}`} custom={direction} variants={contentVariants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="relative z-10" data-id="element-183">

                      {/* Step counter */}
                      <p className="text-sm font-semibold text-white/30 uppercase tracking-[0.2em] mb-6" data-id="element-184">
                        {String(activeStep + 1).padStart(2, '0')} /{' '}
                        {String(timeline.length).padStart(2, '0')}
                      </p>

                      {/* Year */}
                      <p className="text-5xl sm:text-6xl lg:text-7xl font-bold text-carbon-bright tabular-nums leading-none mb-6" data-id="element-185">
                        {entry.year}
                      </p>

                      {/* Icon */}
                      <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6" data-id="element-186">
                        <div className="text-carbon-bright" data-id="element-187">{entry.icon}</div>
                      </div>

                      {/* Badges */}
                      <div className="flex items-center gap-2.5 flex-wrap" data-id="element-188">
                        <span className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${entry.tagColorDark}`} data-id="element-189">

                          {entry.tag}
                        </span>
                        <span className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${config.labelColorDark}`} data-id="element-190">

                          {config.label}
                        </span>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Right: Title + Description */}
                <AnimatePresence mode="wait" custom={direction} data-id="element-191">
                  <motion.div key={`right-${activeStep}`} custom={direction} variants={contentVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.5,
                  delay: 0.08,
                  ease: [0.22, 1, 0.36, 1]
                }} className="relative" data-id="element-192">

                    {/* NOW badge */}
                    {entry.isNow && <motion.div className="mb-5" animate={{
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-193">

                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-carbon-bright/20 border border-carbon-bright/30" data-id="element-194">
                          <span className="w-2.5 h-2.5 rounded-full bg-carbon-bright animate-pulse" data-id="element-195" />
                          <span className="text-sm font-bold text-carbon-bright uppercase tracking-wider" data-id="element-196">
                            Current Phase
                          </span>
                        </span>
                      </motion.div>}

                    {/* Title */}
                    <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1] tracking-tight mb-6" data-id="element-197">
                      {entry.title}
                    </h3>

                    {/* Divider */}
                    <div className="w-16 h-1 rounded-full bg-carbon-bright mb-6" data-id="element-198" />

                    {/* Description */}
                    <p className="text-base sm:text-lg text-white/60 leading-relaxed max-w-lg" data-id="element-199">
                      {entry.description}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Bottom progress bar */}
          <div className="relative w-full h-1 bg-white/5" data-id="element-200">
            <motion.div className="absolute inset-y-0 left-0 bg-carbon-bright rounded-full" style={{
            width: progressWidth
          }} data-id="element-201" />

          </div>

          {/* Bottom info bar */}
          <div className="px-6 sm:px-10 lg:px-16 py-4 flex items-center justify-between" data-id="element-202">
            <div className="flex items-center gap-1.5" data-id="element-203">
              {timeline.map((_, i) => <button key={i} onClick={() => goTo(i)} className={`rounded-full transition-all duration-300 ${i === activeStep ? 'w-8 h-2 bg-carbon-bright' : i < activeStep ? 'w-2 h-2 bg-carbon-bright/40' : 'w-2 h-2 bg-white/15 hover:bg-white/25'}`} aria-label={`Step ${i + 1}: ${timeline[i].year}`} data-id="element-204" />)}
            </div>
            <span className="text-xs font-medium text-white/30 tracking-wider uppercase" data-id="element-205">
              Scroll to explore
            </span>
          </div>
        </div>

        {/* Right edge: vertical step dots */}
        <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 z-20 flex-col items-center gap-0" data-id="element-206">
          {timeline.map((t, i) => {
          const stepConfig = statusConfig[t.status];
          const isPast = i < activeStep;
          const isActive = i === activeStep;
          return <Fragment key={t.year} data-id="element-207">
                <button onClick={() => goTo(i)} className="relative flex items-center gap-3 group focus:outline-none py-1" aria-label={`Go to ${t.year}`} data-id="element-208">

                  {/* Dot */}
                  <div className="relative" data-id="element-209">
                    <motion.div className={`rounded-full transition-all duration-300 ${isActive ? `${stepConfig.dot} w-4 h-4` : isPast ? 'bg-carbon-bright/60 w-2.5 h-2.5' : 'bg-white/15 w-2.5 h-2.5'}`} layout transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 25
                }} data-id="element-210" />

                    {isActive && <motion.div className={`absolute inset-0 rounded-full ${stepConfig.dot}`} animate={{
                  scale: [1, 2, 2],
                  opacity: [0.4, 0, 0]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} data-id="element-211" />}
                  </div>

                  {/* Year label */}
                  <span className={`text-[10px] font-bold tabular-nums transition-all duration-300 ${isActive ? 'text-white opacity-100' : 'text-white/0 group-hover:text-white/40 opacity-0 group-hover:opacity-100'}`} data-id="element-212">

                    {t.year}
                  </span>
                </button>

                {i < timeline.length - 1 && <div className="w-px h-5 bg-white/10 overflow-hidden" data-id="element-213">
                    <motion.div className="w-full bg-carbon-bright/50" animate={{
                height: isPast || isActive ? '100%' : '0%'
              }} transition={{
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-214" />

                  </div>}
              </Fragment>;
        })}
        </div>
      </div>
    </div>;
}
/* ─── Coffee Dashboard Mockup ─── */
function CoffeeDashboardMockup() {
  return <div className="rounded-2xl shadow-2xl border border-[#9aa6af] overflow-hidden bg-white" style={{
    transform: 'perspective(1200px) rotateX(2deg) rotateY(-1deg) scale(0.92)'
  }} data-id="element-215">

      {/* Browser Chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 border-b border-gray-200" data-id="element-216">
        <div className="flex gap-1.5" data-id="element-217">
          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-218" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-219" />
          <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-220" />
        </div>
        <div className="flex-1 mx-4" data-id="element-221">
          <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 font-mono border border-gray-200 max-w-md" data-id="element-222">
            app.carbonsafe.io/coffee
          </div>
        </div>
      </div>

      <div className="flex min-h-[440px]" data-id="element-223">
        {/* Sidebar */}
        <div className="w-44 bg-carbon-deep flex-shrink-0 p-3 hidden sm:block" data-id="element-224">
          <div className="flex items-center gap-2 px-2 py-2 mb-4" data-id="element-225">
            <div className="w-7 h-7 rounded-lg bg-carbon-bright flex items-center justify-center" data-id="element-226">
              <span className="text-white text-xs font-bold" data-id="element-227">CS</span>
            </div>
            <span className="text-white text-sm font-bold" data-id="element-228">CarbonSafe</span>
          </div>
          <nav className="space-y-0.5" data-id="element-229">
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-white/10 text-white text-xs font-medium" data-id="element-230">
              <LayoutDashboardIcon className="w-3.5 h-3.5" data-id="element-231" />
              Dashboard
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-232">
              <LeafIcon className="w-3.5 h-3.5" data-id="element-233" />
              Farms
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-234">
              <FolderIcon className="w-3.5 h-3.5" data-id="element-235" />
              Lots
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-236">
              <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-237" />
              Compliance
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-238">
              <BarChart3Icon className="w-3.5 h-3.5" data-id="element-239" />
              Reports
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-240">
              <SettingsIcon className="w-3.5 h-3.5" data-id="element-241" />
              Settings
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-5 bg-gray-50 overflow-hidden" data-id="element-242">
          {/* Header */}
          <div className="flex items-center justify-between mb-4" data-id="element-243">
            <div data-id="element-244">
              <h3 className="text-sm font-bold text-carbon-deep" data-id="element-245">
                Coffee Traceability | Mt. Elgon Region
              </h3>
              <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-246">
                Last synced 8 min ago
              </p>
            </div>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-id="element-247">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-248" />
              EUDR Compliant
            </span>
          </div>

          {/* Mini Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4" data-id="element-249">
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-250">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-251">
                Registered Farms
              </p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-252">
                12,847
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-253">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-254">
                GPS Mapped
              </p>
              <p className="text-lg font-bold text-carbon-bright mt-0.5" data-id="element-255">
                98.2%
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-256">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-257">
                Active Lots
              </p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-258">342</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-259">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-260">
                EUDR Score
              </p>
              <p className="text-lg font-bold text-carbon-bright mt-0.5" data-id="element-261">A+</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-2.5" data-id="element-262">
            {/* Compliance Status Table */}
            <div className="lg:col-span-3 bg-white rounded-lg border border-[#9aa6af] overflow-hidden" data-id="element-263">
              <div className="px-3 py-2.5 border-b border-[#9aa6af] flex items-center justify-between" data-id="element-264">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-265">
                  Compliance Status
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-266">4 requirements</p>
              </div>
              <div className="divide-y divide-gray-50" data-id="element-267">
                <div className="flex items-center gap-3 px-3 py-2.5" data-id="element-268">
                  <CheckCircle2Icon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-269" />
                  <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-270">
                    Farm GPS Coordinates
                  </span>
                  <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full" data-id="element-271">
                    Verified
                  </span>
                </div>
                <div className="flex items-center gap-3 px-3 py-2.5" data-id="element-272">
                  <CheckCircle2Icon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-273" />
                  <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-274">
                    Deforestation-Free
                  </span>
                  <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full" data-id="element-275">
                    Verified
                  </span>
                </div>
                <div className="flex items-center gap-3 px-3 py-2.5" data-id="element-276">
                  <CheckCircle2Icon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-277" />
                  <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-278">
                    UCDA Quality Grade
                  </span>
                  <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full" data-id="element-279">
                    Verified
                  </span>
                </div>
                <div className="flex items-center gap-3 px-3 py-2.5" data-id="element-280">
                  <CheckCircle2Icon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" data-id="element-281" />
                  <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-282">
                    Carbon Footprint Calc
                  </span>
                  <span className="text-[10px] text-blue-600 font-semibold bg-blue-50 px-2 py-0.5 rounded-full" data-id="element-283">
                    Processing
                  </span>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="lg:col-span-2 bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-284">
              <div className="flex items-center justify-between mb-2.5" data-id="element-285">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-286">
                  Farm Locations
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-287">Mt. Elgon</p>
              </div>
              <div className="relative h-[140px] rounded-lg bg-emerald-50 overflow-hidden" data-id="element-288">
                {/* Grid lines */}
                <div className="absolute inset-0 opacity-20" data-id="element-289">
                  {[...Array(6)].map((_, i) => <div key={`v-${i}`} className="absolute border-r border-emerald-300" style={{
                  left: `${i * 20}%`,
                  top: 0,
                  width: '1px',
                  height: '100%'
                }} data-id="element-290" />)}
                  {[...Array(5)].map((_, i) => <div key={`h-${i}`} className="absolute border-b border-emerald-300" style={{
                  top: `${i * 25}%`,
                  left: 0,
                  width: '100%',
                  height: '1px'
                }} data-id="element-291" />)}
                </div>
                {/* Farm cluster dots */}
                <div className="absolute w-3 h-3 rounded-full bg-carbon-bright shadow-sm shadow-carbon-bright/30" style={{
                left: '25%',
                top: '30%'
              }} data-id="element-292" />

                <div className="absolute w-2.5 h-2.5 rounded-full bg-carbon-bright shadow-sm shadow-carbon-bright/30" style={{
                left: '40%',
                top: '45%'
              }} data-id="element-293" />

                <div className="absolute w-2 h-2 rounded-full bg-carbon-bright/80 shadow-sm" style={{
                left: '55%',
                top: '25%'
              }} data-id="element-294" />

                <div className="absolute w-3 h-3 rounded-full bg-carbon-bright shadow-sm shadow-carbon-bright/30" style={{
                left: '65%',
                top: '55%'
              }} data-id="element-295" />

                <div className="absolute w-2 h-2 rounded-full bg-carbon-bright/70 shadow-sm" style={{
                left: '30%',
                top: '65%'
              }} data-id="element-296" />

                <div className="absolute w-2.5 h-2.5 rounded-full bg-carbon-bright shadow-sm shadow-carbon-bright/30" style={{
                left: '75%',
                top: '35%'
              }} data-id="element-297" />

                <div className="absolute w-2 h-2 rounded-full bg-carbon-bright/60 shadow-sm" style={{
                left: '50%',
                top: '70%'
              }} data-id="element-298" />

                <div className="absolute w-1.5 h-1.5 rounded-full bg-amber-500 shadow-sm" style={{
                left: '80%',
                top: '60%'
              }} data-id="element-299" />

                {/* Label */}
                <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded px-2 py-1 text-[9px] font-semibold text-carbon-deep shadow-sm" data-id="element-300">
                  12,847 farms mapped
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
/* ─── Main Component ─── */
export function CoffeeRegulation() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, {
    once: true,
    margin: '-80px'
  });
  const checklistRef = useRef(null);
  const checklistInView = useInView(checklistRef, {
    once: true,
    margin: '-60px'
  });
  const bottomRef = useRef(null);
  const bottomInView = useInView(bottomRef, {
    once: true,
    margin: '-80px'
  });
  return <section className="w-full" data-id="element-301">
      {/* Header */}
      <div ref={headerRef} className="bg-white py-20 sm:py-28 pb-16 sm:pb-20" data-id="element-302">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-303">
          <motion.div className="text-center" initial={{
          opacity: 0,
          y: 20
        }} animate={headerInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-304">

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-305">
              Uganda's coffee compliance is{' '}
              <span className="relative inline-block" data-id="element-306">
                <span className="relative z-10 text-carbon-bright" data-id="element-307">
                  evolving fast
                </span>
                <span className="absolute bottom-1 left-0 right-0 h-[35%] bg-carbon-bright/15 -z-0 rounded-sm" aria-hidden="true" data-id="element-308" />

              </span>
            </h2>
            <p className="mt-4 text-lg text-carbon-deep max-w-3xl mx-auto" data-id="element-309">
              From foundational quality standards to EU-mandated digital
              traceability, here's how Uganda's regulatory landscape is
              transforming the coffee sector.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Full-screen immersive timeline */}
      <ImmersiveTimeline data-id="element-310" />

      {/* Bottom section — Platform Preview */}
      <div ref={bottomRef} className="bg-white py-20 sm:py-28" data-id="element-311">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-312">
          {/* Section header */}
          <motion.div className="text-center mb-14" initial={{
          opacity: 0,
          y: 20
        }} animate={bottomInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-313">

            <span className="inline-block text-[11px] font-bold uppercase tracking-widest  bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-314">
              Platform Preview
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-315">
              Automated compliance, verified in{' '}
              <span data-id="element-316">real-time</span>
            </h2>
            <p className="mt-4 text-lg text-carbon-deep max-w-2xl mx-auto" data-id="element-317">
              CarbonSafe automates every coffee traceability requirement, from
              farm GPS mapping to EUDR due diligence, in one verifiable
              platform.
            </p>
          </motion.div>

          {/* Two-column: Dashboard + Image */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center" data-id="element-318">
            {/* Left: Dashboard Mockup */}
            <motion.div initial={{
            opacity: 0,
            x: -40
          }} animate={bottomInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.8,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-319">

              <CoffeeDashboardMockup data-id="element-320" />
            </motion.div>

            {/* Right: Coffee Farmer Image */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={bottomInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.7,
            delay: 0.3,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-321">

              <div className="rounded-2xl overflow-hidden shadow-xl shadow-carbon-deep/10" data-id="element-322">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/f38cb422-8081-435a-bada-933114ce7768/ext-023b54a87180.jpeg" alt="Coffee farmer in East Africa inspecting coffee cherries" className="w-full h-[400px] object-cover" data-id="element-323" />

              </div>
            </motion.div>
          </div>

          {/* Compliance Checklist Download CTA — full width row */}
          <motion.div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6" initial={{
          opacity: 0,
          y: 20
        }} animate={bottomInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.45,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-324">

            <div className="flex items-center gap-4" data-id="element-325">
              <ShieldCheckIcon className="w-6 h-6 text-carbon-bright flex-shrink-0" data-id="element-326" />
              <div data-id="element-327">
                <h3 className="text-base font-bold text-carbon-deep" data-id="element-328">
                  EUDR Compliance Checklist
                </h3>
                <p className="text-sm text-carbon-deep" data-id="element-329">
                  All 8 requirements Ugandan coffee exporters need to meet,
                  automated by CarbonSafe.
                </p>
              </div>
            </div>
            <a href={MAILTO.coffeeChecklist} className="inline-flex items-center gap-2 px-6 py-3 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-md shadow-carbon-bright/20 flex-shrink-0" data-id="element-330">

              <DownloadIcon className="w-4 h-4" data-id="element-331" />
              Download Checklist (PDF)
            </a>
          </motion.div>

          {/* Uganda callout — full width below */}
          <motion.div className="mt-12" initial={{
          opacity: 0,
          y: 20
        }} animate={bottomInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.5,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-332">

            <div className="p-7 rounded-2xl bg-carbon-deep text-white" data-id="element-333">
              <div className="flex items-start gap-4" data-id="element-334">
                <div className="w-12 h-12 rounded-xl bg-carbon-bright/20 flex items-center justify-center flex-shrink-0" data-id="element-335">
                  <MapPinIcon className="w-6 h-6 text-carbon-bright" data-id="element-336" />
                </div>
                <div data-id="element-337">
                  <h3 className="text-lg font-bold" data-id="element-338">Why Uganda matters</h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed" data-id="element-339">
                    Uganda is the world's 7th largest coffee producer and
                    Africa's top exporter, generating $1.3B+ in annual export
                    revenue. Coffee supports 10 million Ugandans across the Mt.
                    Elgon, Rwenzori, and West Nile growing regions. With 60%+ of
                    exports destined for the EU, EUDR compliance is an
                    existential priority.
                  </p>
                  <a href={MAILTO.coffeeReport} className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-carbon-bright hover:text-carbon-light transition-colors" data-id="element-340">

                    Read the full Uganda report
                    <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-341" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}